import React, { useState, useEffect } from "react";
import { Row, Col, message, Card, Table } from "antd";
import CashFlowFilters from "./components/CashFlowFilters";
import CashFlowChart from "./components/CashFlowChart";
import { getEstimatedCashFlow } from "../../core/api/mediaplan";
import dayjs from "dayjs";
const CashFlowEstimation = () => {
  const [cashFlowData, setCashFlowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersMinimized, setFiltersMinimized] = useState(() => {
    // Get the saved state from localStorage or default to false if not found
    const savedState = localStorage.getItem('cashFlowFiltersMinimized');
    return savedState ? JSON.parse(savedState) : false;
  });
  
  // Current filter state (changes as user interacts with filters)
  const [filters, setFilters] = useState({
    startDate: dayjs(),
    endDate: dayjs().add(30, "days"),
    brandIds: [],
    productIds: [],
    productCategoryIds: [],
    campaignIds: [],
    timePeriod: "daily",
    dateRange: [dayjs(), dayjs().add(30, "days")],
  });
  // Applied filter state (only changes when Apply Filters is clicked)
  const [appliedFilters, setAppliedFilters] = useState({
    timePeriod: "daily",
  });

  const fetchCashFlowData = async (filterParams) => {
    setLoading(true);
    try {
      if (!filterParams.dateRange || filterParams.dateRange.length !== 2) {
        message.warning("Please select a valid date range before applying filters");
        setLoading(false);
        return;
      }
      
      const response = await getEstimatedCashFlow(filterParams);
      setCashFlowData(response.data.cash_flow_data || []);
    } catch (error) {
      console.error("Error fetching cash flow data:", error);
      message.error("Failed to fetch cash flow data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(filters.brandIds.length === 0) return;
    fetchCashFlowData(filters);
  }, [filters.brandIds]);
  
  // Effect to sync with localStorage changes for filter minimized state
  useEffect(() => {
    const handleStorageChange = () => {
      const savedState = localStorage.getItem('cashFlowFiltersMinimized');
      if (savedState !== null) {
        setFiltersMinimized(JSON.parse(savedState));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setAppliedFilters({
      ...appliedFilters,
      timePeriod: newFilters.timePeriod
    });
    fetchCashFlowData(newFilters);
  };

  // Handle filter clear
  const handleClearFilters = () => {
    const defaultFilters = {
      startDate: dayjs(),
      endDate: dayjs().add(30, "days"),
      brandIds: [],
      productIds: [],
      productCategoryIds: [],
      campaignIds: [],
      timePeriod: "daily",
      dateRange: [dayjs(), dayjs().add(30, "days")],
    };
    setFilters(defaultFilters);
    setAppliedFilters({
      ...appliedFilters,
      timePeriod: "daily"
    });
    fetchCashFlowData(defaultFilters);
  };

  // Format currency for table
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Format date for table based on applied time period
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    if (appliedFilters.timePeriod === 'daily') {
      return dayjs(dateStr).format('MMM D, YYYY');
    } else if (appliedFilters.timePeriod === 'weekly') {
      // Format: "Week X of YYYY"
      const parts = dateStr.split('-W');
      return `Week ${parts[1]} of ${parts[0]}`;
    } else if (appliedFilters.timePeriod === 'monthly') {
      // Format: "Month YYYY"
      const date = dayjs(dateStr + '-01');
      return date.format('MMMM YYYY');
    } else {
      // Yearly
      return dateStr;
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text) => formatDate(text),
      sorter: (a, b) => {
        if (!a.date || !b.date) return 0;
        return dayjs(a.date).unix() - dayjs(b.date).unix();
      },
    },
    {
      title: 'Budgeted Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (text) => formatCurrency(text),
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: 'Actual Revenue',
      dataIndex: 'actual_revenue',
      key: 'actual_revenue',
      render: (text) => formatCurrency(text),
      sorter: (a, b) => a.actual_revenue - b.actual_revenue,
    },
    {
      title: 'Budgeted Cost',
      dataIndex: 'cost',
      key: 'cost',
      render: (text) => formatCurrency(text),
      sorter: (a, b) => a.cost - b.cost,
    },
    {
      title: 'Actual Cost',
      dataIndex: 'actual_cost',
      key: 'actual_cost',
      render: (text) => formatCurrency(text),
      sorter: (a, b) => a.actual_cost - b.actual_cost,
    },
    {
      title: 'Budgeted Profit',
      dataIndex: 'profit',
      key: 'profit',
      render: (text) => formatCurrency(text),
      sorter: (a, b) => a.profit - b.profit,
    },
    {
      title: 'Actual Profit',
      dataIndex: 'actual_profit',
      key: 'actual_profit',
      render: (text) => formatCurrency(text),
      sorter: (a, b) => a.actual_profit - b.actual_profit,
    },
    {
      title: 'Budgeted Margin',
      key: 'margin',
      render: (_, record) => {
        const margin = record.revenue > 0 ? (record.profit / record.revenue) * 100 : 0;
        return `${margin.toFixed(2)}%`;
      },
      sorter: (a, b) => {
        const marginA = a.revenue > 0 ? (a.profit / a.revenue) * 100 : 0;
        const marginB = b.revenue > 0 ? (b.profit / b.revenue) * 100 : 0;
        return marginA - marginB;
      },
    },
    {
      title: 'Actual Margin',
      key: 'actual_margin',
      render: (_, record) => {
        const margin = record.actual_revenue > 0 ? (record.actual_profit / record.actual_revenue) * 100 : 0;
        return `${margin.toFixed(2)}%`;
      },
      sorter: (a, b) => {
        const marginA = a.actual_revenue > 0 ? (a.actual_profit / a.actual_revenue) * 100 : 0;
        const marginB = b.actual_revenue > 0 ? (b.actual_profit / b.actual_revenue) * 100 : 0;
        return marginA - marginB;
      },
    },
  ];

  // Calculate totals
  const calculateTotals = () => {
    if (!cashFlowData || cashFlowData.length === 0) return {};
    
    const totalBudgetedRevenue = cashFlowData.reduce((sum, item) => sum + item.revenue, 0);
    const totalBudgetedCost = cashFlowData.reduce((sum, item) => sum + item.cost, 0);
    const totalBudgetedProfit = cashFlowData.reduce((sum, item) => sum + item.profit, 0);
    const totalBudgetedMargin = totalBudgetedRevenue > 0 ? (totalBudgetedProfit / totalBudgetedRevenue) * 100 : 0;
    
    const totalActualRevenue = cashFlowData.reduce((sum, item) => sum + item.actual_revenue, 0);
    const totalActualCost = cashFlowData.reduce((sum, item) => sum + item.actual_cost, 0);
    const totalActualProfit = cashFlowData.reduce((sum, item) => sum + item.actual_profit, 0);
    const totalActualMargin = totalActualRevenue > 0 ? (totalActualProfit / totalActualRevenue) * 100 : 0;
    
    return {
      budgetedRevenue: formatCurrency(totalBudgetedRevenue),
      budgetedCost: formatCurrency(totalBudgetedCost),
      budgetedProfit: formatCurrency(totalBudgetedProfit),
      budgetedMargin: `${totalBudgetedMargin.toFixed(2)}%`,
      actualRevenue: formatCurrency(totalActualRevenue),
      actualCost: formatCurrency(totalActualCost),
      actualProfit: formatCurrency(totalActualProfit),
      actualMargin: `${totalActualMargin.toFixed(2)}%`,
    };
  };

  const totals = calculateTotals();

  return (
    <div className="page-wrapper">
      <div className="p-2">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Cash Flow</h3>
            </div>
          </div>
        </div>

        <Row gutter={16} >
          <Col xs={24} md={filtersMinimized ? 1 : 8} lg={filtersMinimized ? 1 : 6} style={{ transition: 'all 0.3s ease' }}>
            <CashFlowFilters
              filters={filters}
              setFilters={setFilters}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
              setFiltersMinimized={setFiltersMinimized}
            />
          </Col>
          <Col xs={24} md={filtersMinimized ? 23 : 16} lg={filtersMinimized ? 23 : 18} style={{ transition: 'all 0.3s ease' }}>
            <CashFlowChart
              data={cashFlowData}
              loading={loading}
              timePeriod={appliedFilters.timePeriod}
            />
            
            <Card title="Cash Flow Data" style={{ marginBottom: 16 }}>
              <Table
                dataSource={cashFlowData}
                columns={columns}
                rowKey="date"
                loading={loading}
                pagination={{ pageSize: 10 }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>{totals.budgetedRevenue}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2}><strong>{totals.actualRevenue}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3}><strong>{totals.budgetedCost}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4}><strong>{totals.actualCost}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5}><strong>{totals.budgetedProfit}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={6}><strong>{totals.actualProfit}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={7}><strong>{totals.budgetedMargin}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={8}><strong>{totals.actualMargin}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CashFlowEstimation;
